import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { userService } from '../../utils/supabaseQueries';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import LoadingIndicator from '../../components/ui/LoadingIndicator';
import UserTable from './components/UserTable';
import UserFilters from './components/UserFilters';
import UserStats from './components/UserStats';
import UserModal from './components/UserModal';
import BulkActions from './components/BulkActions';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const UserManagement = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalMode, setModalMode] = useState('view');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBulkActionsOpen, setIsBulkActionsOpen] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);

  const [filters, setFilters] = useState({
    search: '',
    subscription: 'all',
    status: 'all',
    activity: 'all',
    dateFrom: '',
    dateTo: ''
  });

  // Load users and stats on component mount
  useEffect(() => {
    const loadUserData = async () => {
      if (!user || user?.user_metadata?.role !== 'admin') {
        setError('Access denied. Admin privileges required.');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        // Load all users (admin only)
        const { data: usersData, error: usersError } = await userService?.getAllUsers();
        if (usersError) throw usersError;

        // Transform user data to match UI expectations
        const transformedUsers = usersData?.map((userProfile, index) => ({
          id: userProfile?.id,
          name: userProfile?.full_name || 'Unknown User',
          email: userProfile?.email,
          avatar: userProfile?.avatar_url || `https://images.unsplash.com/photo-${1700000000000 + index * 1000000}`,
          avatarAlt: `Professional headshot of ${userProfile?.full_name || 'user'}`,
          subscription: userProfile?.role === 'admin' ? 'enterprise' : 
                      userProfile?.role === 'manager' ? 'pro' : 
                      userProfile?.role === 'filmmaker' ? 'basic' : 'free',
          status: userProfile?.is_active ? 'active' : 'inactive',
          role: userProfile?.role || 'user',
          registrationDate: userProfile?.created_at?.split('T')?.[0] || new Date()?.toISOString()?.split('T')?.[0],
          lastActivity: calculateLastActivity(userProfile?.updated_at),
          notifications: { 
            email: true, 
            sms: userProfile?.role === 'admin', 
            push: userProfile?.is_active 
          },
          company: userProfile?.company,
          bio: userProfile?.bio
        })) || [];

        setUsers(transformedUsers);
        setFilteredUsers(transformedUsers);

        // Calculate stats
        const totalUsers = transformedUsers?.length || 0;
        const newThisMonth = transformedUsers?.filter(u => {
          const userDate = new Date(u?.registrationDate);
          const now = new Date();
          return userDate?.getMonth() === now?.getMonth() && userDate?.getFullYear() === now?.getFullYear();
        })?.length || 0;

        const activeUsers = transformedUsers?.filter(u => u?.status === 'active')?.length || 0;
        const churnRate = totalUsers > 0 ? ((totalUsers - activeUsers) / totalUsers * 100)?.toFixed(1) : 0;

        setStats({
          totalUsers,
          newThisMonth,
          activeUsers,
          churnRate: parseFloat(churnRate)
        });

      } catch (error) {
        console.error('Error loading user data:', error);
        setError('Failed to load user data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    loadUserData();
  }, [user]);

  // Helper function to calculate last activity
  const calculateLastActivity = (updatedAt) => {
    if (!updatedAt) return '1 month ago';
    
    const now = new Date();
    const lastUpdate = new Date(updatedAt);
    const diffInHours = Math.floor((now - lastUpdate) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return '30 minutes ago';
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
    
    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4) return `${diffInWeeks} week${diffInWeeks !== 1 ? 's' : ''} ago`;
    
    return '1 month ago';
  };

  // Filter users based on current filters
  useEffect(() => {
    let filtered = [...users];

    // Search filter
    if (filters?.search) {
      filtered = filtered?.filter((user) =>
        user?.name?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
        user?.email?.toLowerCase()?.includes(filters?.search?.toLowerCase())
      );
    }

    // Subscription filter
    if (filters?.subscription !== 'all') {
      filtered = filtered?.filter((user) => user?.subscription === filters?.subscription);
    }

    // Status filter
    if (filters?.status !== 'all') {
      filtered = filtered?.filter((user) => user?.status === filters?.status);
    }

    // Activity filter
    if (filters?.activity !== 'all') {
      filtered = filtered?.filter((user) => {
        switch (filters?.activity) {
          case 'today':
            return user?.lastActivity?.includes('hour') || user?.lastActivity?.includes('minute');
          case 'week':
            return !user?.lastActivity?.includes('week') && !user?.lastActivity?.includes('month');
          case 'month':
            return !user?.lastActivity?.includes('month');
          case 'inactive30':
            return user?.lastActivity?.includes('week') || user?.lastActivity?.includes('month');
          default:
            return true;
        }
      });
    }

    // Date range filter
    if (filters?.dateFrom || filters?.dateTo) {
      filtered = filtered?.filter((user) => {
        const userDate = new Date(user?.registrationDate);
        const fromDate = filters?.dateFrom ? new Date(filters?.dateFrom) : new Date('2000-01-01');
        const toDate = filters?.dateTo ? new Date(filters?.dateTo) : new Date();
        return userDate >= fromDate && userDate <= toDate;
      });
    }

    setFilteredUsers(filtered);
  }, [users, filters]);

  const handleUserAction = async (action, targetUser) => {
    try {
      switch (action) {
        case 'view':
          setSelectedUser(targetUser);
          setModalMode('view');
          setIsModalOpen(true);
          break;
        case 'edit':
          setSelectedUser(targetUser);
          setModalMode('edit');
          setIsModalOpen(true);
          break;
        case 'message': console.log('Send message to:', targetUser?.name);
          // Implement messaging functionality
          break;
        case 'suspend':
          const { error: suspendError } = await userService?.updateUserProfile(targetUser?.id, {
            is_active: false
          });
          if (suspendError) throw suspendError;
          setUsers((prev) => prev?.map((u) =>
            u?.id === targetUser?.id ? { ...u, status: 'inactive' } : u
          ));
          break;
        case 'activate':
          const { error: activateError } = await userService?.updateUserProfile(targetUser?.id, {
            is_active: true
          });
          if (activateError) throw activateError;
          setUsers((prev) => prev?.map((u) =>
            u?.id === targetUser?.id ? { ...u, status: 'active' } : u
          ));
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Error performing user action:', error);
      setError(`Failed to ${action} user. Please try again.`);
    }
  };

  const handleUserSelect = (userId, isSelected) => {
    if (isSelected) {
      setSelectedUsers((prev) => [...prev, userId]);
    } else {
      setSelectedUsers((prev) => prev?.filter((id) => id !== userId));
    }
  };

  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      setSelectedUsers(filteredUsers?.map((user) => user?.id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleBulkAction = async (actionData) => {
    try {
      console.log('Bulk action:', actionData);
      
      // Implement bulk actions based on actionData
      if (actionData?.action === 'activate') {
        await Promise.all(
          selectedUsers?.map(userId => 
            userService?.updateUserProfile(userId, { is_active: true })
          )
        );
        
        setUsers(prev => prev?.map(user => 
          selectedUsers?.includes(user?.id) ? { ...user, status: 'active' } : user
        ));
      } else if (actionData?.action === 'deactivate') {
        await Promise.all(
          selectedUsers?.map(userId => 
            userService?.updateUserProfile(userId, { is_active: false })
          )
        );
        
        setUsers(prev => prev?.map(user => 
          selectedUsers?.includes(user?.id) ? { ...user, status: 'inactive' } : user
        ));
      }

      setSelectedUsers([]);
    } catch (error) {
      console.error('Error performing bulk action:', error);
      setError('Failed to perform bulk action. Please try again.');
    }
  };

  const handleExport = () => {
    try {
      // Create CSV data
      const csvData = filteredUsers?.map(user => ({
        name: user?.name,
        email: user?.email,
        role: user?.role,
        status: user?.status,
        subscription: user?.subscription,
        registrationDate: user?.registrationDate,
        company: user?.company || ''
      }));

      // Convert to CSV string
      const headers = Object.keys(csvData?.[0] || {});
      const csvString = [
        headers?.join(','),
        ...csvData?.map(user => headers?.map(header => `"${user?.[header] || ''}"`)?.join(','))
      ]?.join('\n');

      // Download CSV
      const blob = new Blob([csvString], { type: 'text/csv' });
      const url = window.URL?.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `users-export-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
      document.body?.appendChild(a);
      a?.click();
      document.body?.removeChild(a);
      window.URL?.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting users:', error);
      setError('Failed to export user data. Please try again.');
    }
  };

  const handleUserSave = async (userData) => {
    try {
      const { error } = await userService?.updateUserProfile(selectedUser?.id, {
        full_name: userData?.name,
        email: userData?.email,
        role: userData?.role,
        company: userData?.company,
        bio: userData?.bio,
        is_active: userData?.status === 'active'
      });

      if (error) throw error;

      setUsers((prev) => prev?.map((user) =>
        user?.id === selectedUser?.id ? { 
          ...user, 
          ...userData,
          status: userData?.status 
        } : user
      ));
      
      setIsModalOpen(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Error saving user:', error);
      setError('Failed to save user changes. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header userRole="admin" userName={user?.user_metadata?.full_name || user?.email} />
        <main className="pt-16">
          <div className="container mx-auto px-4 py-8">
            <LoadingIndicator message="Loading user management..." />
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header userRole="admin" userName={user?.user_metadata?.full_name || user?.email} />
        <main className="pt-16">
          <div className="container mx-auto px-4 py-8">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <Icon name="AlertTriangle" size={48} className="text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-red-800 mb-2">Error Loading Users</h3>
              <p className="text-red-600 mb-4">{error}</p>
              <Button onClick={() => window.location?.reload()}>
                Try Again
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header userRole="admin" userName={user?.user_metadata?.full_name || user?.email} />
      <main className="pt-16">
        <div className="container mx-auto px-4 py-8">
          <div className="mb-8">
            <Breadcrumb />
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-foreground">User Management</h1>
                <p className="text-muted-foreground mt-2">
                  Manage user accounts, subscriptions, and platform access
                </p>
              </div>
              
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  iconName={showStats ? "ChevronUp" : "BarChart3"}
                  onClick={() => setShowStats(!showStats)}
                >
                  {showStats ? 'Hide Stats' : 'Show Stats'}
                </Button>
                
                <Button
                  variant="default"
                  iconName="UserPlus"
                  onClick={() => {
                    setSelectedUser(null);
                    setModalMode('edit');
                    setIsModalOpen(true);
                  }}
                >
                  Add User
                </Button>
              </div>
            </div>
          </div>

          {/* Statistics Panel */}
          {showStats && (
            <div className="mb-8">
              <UserStats stats={stats} />
            </div>
          )}

          {/* Filters */}
          <UserFilters
            filters={filters}
            onFiltersChange={setFilters}
            onExport={handleExport}
            onBulkEmail={() => setIsBulkActionsOpen(true)}
            selectedCount={selectedUsers?.length}
          />

          {/* Results Summary */}
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-muted-foreground">
              Showing {filteredUsers?.length} of {users?.length} users
            </p>
            
            {selectedUsers?.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                iconName="Settings"
                onClick={() => setIsBulkActionsOpen(true)}
              >
                Bulk Actions ({selectedUsers?.length})
              </Button>
            )}
          </div>

          {/* User Table */}
          <UserTable
            users={filteredUsers}
            onUserAction={handleUserAction}
            selectedUsers={selectedUsers}
            onUserSelect={handleUserSelect}
            onSelectAll={handleSelectAll}
          />

          {/* Pagination */}
          {filteredUsers?.length > 0 && (
            <div className="flex items-center justify-between mt-6">
              <p className="text-sm text-muted-foreground">
                Page 1 of 1
              </p>
              <div className="flex items-center space-x-2">
                <Button variant="outline" size="sm" disabled>
                  <Icon name="ChevronLeft" size={16} />
                  Previous
                </Button>
                <Button variant="outline" size="sm" disabled>
                  Next
                  <Icon name="ChevronRight" size={16} />
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* User Modal */}
      <UserModal
        user={selectedUser}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleUserSave}
        mode={modalMode}
      />

      {/* Bulk Actions Modal */}
      {isBulkActionsOpen && (
        <BulkActions
          selectedUsers={selectedUsers}
          onBulkAction={handleBulkAction}
          onClose={() => setIsBulkActionsOpen(false)}
        />
      )}
    </div>
  );
};

export default UserManagement;